import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { axiosClient } from '../../../core/axios-client'

const updateBike = createAsyncThunk(
    'updateBike',
    async ({ id, ...rest }) => {
        const response = await axiosClient.put(`/bikes/${id}`, rest)
        return response.data;
    }
)

const updateBikeRates = createAsyncThunk(
    'bikes/updateBikeRates',
    async ({ id, company_rates }) => {
        const response = await axiosClient.patch(`/bikes/${id}/rates/`, { company_rates })
        return response.data
    }
)

const updateBikeModel = createAsyncThunk(
    'bikes/updateBikeModel',
    async ({ bike_model_id, ...rest }) => {
        const form = new FormData()
        Object.entries(rest).forEach(([key, value]) => {
            if (value !== undefined && value !== null) form.append(key, value)
        })
        const response = await axiosClient.patch(`/bike-models/${bike_model_id}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        return response.data
    }
)

const fetchBike = createAsyncThunk(
  'fetchBike',
  async ({ id }) => {
    const response = await axiosClient.get(`/bikes/${id}`)
    return response.data;
  }
)

const fetchBikeCommands = createAsyncThunk(
  'bikes/fetchBikeCommands',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/bikes/${id}/commands/`)
      return response.data?.items || []
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const sendBikeCommand = createAsyncThunk(
  'bikes/sendBikeCommand',
  async ({ id, command }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/bikes/${id}/commands/${command}`)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const fetchBikes = createAsyncThunk(
  'fetchBikes',
  async () => {
    const response = await axiosClient.get(`/bikes/`)
    return response?.data?.results || []
  }
)

const fetchBikeRequests = createAsyncThunk(
  'fetchBikeRequests',
  async ({ id, ...rest }) => {
    const response = await axiosClient.get('/service-requests/', {
      params: {
        unit_id: id,
        ...rest
      }
    })
    return response.data;
  }
)

const fetchBikeModels = createAsyncThunk(
  'fetchBikeModels',
  async () => {
    const response = await axiosClient.get('/bike-models/')
    return response.data.results;
  }
)

const fetchBikeClientHistory = createAsyncThunk(
  'fetchBikeClientHistory',
  async ({ id, ...rest }) => {
    const response = await axiosClient.get(`/bikes/${id}/client-history/`, {
      params: {
        ...rest
      }
    })
    return response.data;
  }
)

const fetchBikeTravelHistory = createAsyncThunk(
    'bike/fetchTravelHistory',
    async ({ id, date_from, date_to, source }, { rejectWithValue }) => {
      try {
        const res = await axiosClient.get(
          `/bikes/${id}/location-history/geojson/`,
          { params: { date_from, date_to, source, only_points: 1 } }
        );
        return res.data;
      } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
      }
    }
);

const fetchLocationSourceTypes = createAsyncThunk(
    'bike/fetchLocationSourceTypes',
    async ({ id }, { rejectWithValue }) => {
      try {
        const res = await axiosClient.get(`/bikes/${id}/location-history/source-types`);
        return res.data;
      } catch (err) {
        return rejectWithValue(err.response?.data || err.message);
      }
    }
);

const fetchBikeSimLocationHistory = createAsyncThunk(
    'bike/fetchSimLocationHistory',
    async ({ id, date_from, date_to }, { rejectWithValue }) => {
      try {
        const res = await axiosClient.get(
          `/bikes/${id}/sim-location-history/geojson/`,
          { params: { date_from, date_to, only_lines: 1 } }
        );
        return res.data;
      } catch (err) {
          return rejectWithValue(err.response?.data || err.message);
      }
    }
);

const engineResume = createAsyncThunk(
  'engineResume',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/bikes/${id}/commands/engine-on`)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const engineStop = createAsyncThunk(
  'engineStop',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(`/bikes/${id}/commands/engine-off`)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const batteryLock = createAsyncThunk(
    'batteryLock',
    async ({ id }, { rejectWithValue }) => {
      try {
        const response = await axiosClient.post(`/bikes/${id}/commands/battery-lock`)
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message)
      }
    }
)

const batteryUnlock = createAsyncThunk(
    'batteryUnlock',
    async ({ id }, { rejectWithValue }) => {
      try {
        const response = await axiosClient.post(`/bikes/${id}/commands/battery-unlock`)
        return response.data;
      } catch (error) {
        return rejectWithValue(error.response?.data || error.message)
      }
    }
)

const fetchEventTypes = createAsyncThunk(
  'bikes/fetchEventTypes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosClient.get('/bikes/events/types')
      return res.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const fetchBikeEvents = createAsyncThunk(
  'bikes/fetchBikeEvents',
  async ({ id, event_types, date_from, date_to, page = 1, page_size = 10 }, { rejectWithValue }) => {
    try {
      const params = {
        page,
        page_size
      }
      if (event_types) {
        params.event_types = Array.isArray(event_types) ? event_types.join(',') : event_types
      }
      if (date_from) params.date_from = date_from
      if (date_to) params.date_to = date_to

      const res = await axiosClient.get(`/bikes/${id}/events/history`, { params })
      return res.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const initialState = {
  bike: null,
  bikes: [],
  bikeClientHistory: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0
  },
  bikeRequests: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0
  },
  bikesModels: [],
  bikeTravelHistoryGeoJSON: null,
  bikeSimLocationHistoryGeoJSON: null,
  locationSourceTypes: [],
  bikeEvents: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0
  },
  eventTypes: [],
  bikeCommands: []
}

export const slice = createSlice({
  name: 'bike',
  initialState,
  reducers: {
    resetBike(state) {
      state.bike = null
      state.bikeClientHistory = {
        items: [],
        page: 1,
        page_size: 10,
        count: 0
      }
      state.bikeRequests = {
        items: [],
        page: 1,
        page_size: 10,
        count: 0
      }
      state.bikeEvents = {
        items: [],
        page: 1,
        page_size: 10,
        count: 0
      }
      state.eventTypes = []
      state.bikeCommands = []
    },
    setBikeRequests(state, { payload }) {
      state.bikeRequests = { ...state.bikeRequests, ...payload }
    },
    setBikeClientHistory(state, { payload }) {
      state.bikeClientHistory = { ...state.bikeClientHistory, ...payload }
    },

    setBikeEvents(state, { payload }) {
      state.bikeEvents = { ...state.bikeEvents, ...payload }
    },

    setEventTypes(state, { payload }) {
      state.eventTypes = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBike.fulfilled, (state, { payload }) => {
      state.bike = payload
    })

    builder.addCase(fetchBikes.fulfilled, (state, { payload }) => {
      state.bikes = payload
    })

    builder.addCase(fetchBikeCommands.fulfilled, (state, { payload }) => {
      state.bikeCommands = payload || []
    })

    builder.addCase(fetchBikeRequests.fulfilled, (state, { payload: { results, count }}) => {
      state.bikeRequests = {
        ...state.bikeRequests,
        items: results,
        count
      }
    })

    builder.addCase(fetchBikeClientHistory.fulfilled, (state, { payload: { results, count }}) => {
      state.bikeClientHistory = {
        ...state.bikeClientHistory,
        items: results,
        count
      }
    })

    builder.addCase(fetchBikeModels.fulfilled, (state, { payload }) => {
      state.bikesModels = payload
    })

    builder.addCase(fetchBikeTravelHistory.fulfilled, (state, { payload }) => {
        state.bikeTravelHistoryGeoJSON = payload;
    });

    builder.addCase(fetchLocationSourceTypes.fulfilled, (state, { payload }) => {
      state.locationSourceTypes = payload;
    });

    builder.addCase(fetchBikeSimLocationHistory.fulfilled, (state, { payload }) => {
      state.bikeSimLocationHistoryGeoJSON = payload;
    });

    builder.addCase(fetchEventTypes.fulfilled, (state, { payload }) => {
      state.eventTypes = payload || []
    })

    builder.addCase(fetchBikeEvents.fulfilled, (state, { payload: { results, count }}) => {
      state.bikeEvents = {
        ...state.bikeEvents,
        items: results,
        count
      }
    })
  }
})

const {
  resetBike,
  setBikeClientHistory,
  setBikeRequests,
  setBikeEvents,
  setEventTypes
} = slice.actions

export {
  fetchBike,
  fetchBikeCommands,
  sendBikeCommand,
  fetchBikes,
  fetchBikeRequests,
  resetBike,
  fetchBikeModels,
  setBikeClientHistory,
  setBikeRequests,
  fetchBikeClientHistory,
  fetchBikeTravelHistory,
  fetchBikeSimLocationHistory,
  fetchLocationSourceTypes,
  engineResume,
  engineStop,
  batteryLock,
  batteryUnlock,
  updateBike,
  updateBikeRates,
  updateBikeModel,
  fetchEventTypes,
  fetchBikeEvents,
  setBikeEvents,
  setEventTypes
}

export default slice.reducer;
